import { useQuery } from "@tanstack/react-query";
import { getMyEffectivePermissions } from "../api/rbac-api";
import type {
	EffectiveCapabilityFlags,
	UserEffectivePermissions,
} from "../types/RolesPermissions.type";

export const MY_EFFECTIVE_PERMISSIONS_KEY = ["rbac", "me", "permissions"] as const;

/**
 * PRO-709 — the current user's RBAC v2 capability for a single module.
 *
 * Feature surfaces gated on legacy signals (`company_admin`, `is_admin`,
 * `administers_company_rbac`) instead of this payload, which is what the ticket
 * reports: a Bidder Support holds `company_profile = view` yet Corporate
 * Documents rendered empty, because `allowedDocument` asked whether they were a
 * company admin rather than whether they could view the module.
 *
 * ## Deny while loading, on error, and for unknown modules
 *
 * `canX` is false until the payload arrives. That is deliberate and is the only
 * safe default: a hook that returned `true` while loading would flash edit
 * controls to a view-only user on every page load, and any surface that mounts
 * before the query settles would grant access it later has to retract. Callers
 * that need to distinguish "denied" from "not yet known" — e.g. to render a
 * spinner rather than an empty state — read `isLoading`.
 *
 * A module key absent from the payload also resolves to false rather than
 * throwing: the server sends the full grid including `none` cells, so a missing
 * key means the FE asked for a module the backend does not know, and denying is
 * the correct answer to a question that has no server-side truth.
 *
 * CAPABILITY only, mirroring `User::moduleCan()` on the API side: this answers
 * "may this user's standing do $verb on $module at all", never "for THIS
 * company". Tenancy stays with the server. The FE gate is a UX affordance —
 * every surface it hides must also be enforced by a route gate or policy, since
 * hiding a button is not access control.
 */
export interface ModuleCapability {
	canView: boolean;
	canCreate: boolean;
	canEdit: boolean;
	canDelete: boolean;
	/** True until the permissions payload resolves; every `canX` is false meanwhile. */
	isLoading: boolean;
	/** Raw flags, for the rare surface needing `approve`/`close`/`comment`. */
	can: EffectiveCapabilityFlags | undefined;
}

export const useMyEffectivePermissions = () =>
	useQuery<UserEffectivePermissions>({
		queryKey: MY_EFFECTIVE_PERMISSIONS_KEY,
		queryFn: getMyEffectivePermissions,
		// Standing permissions change only when an admin reassigns a role, so a
		// short cache avoids refetching this on every gated surface in a page.
		staleTime: 5 * 60 * 1000,
	});

export const useModuleCan = (moduleKey: string): ModuleCapability => {
	const { data, isLoading } = useMyEffectivePermissions();

	const can = data?.modules?.[moduleKey]?.can;

	return {
		canView: can?.view === true,
		canCreate: can?.create === true,
		canEdit: can?.edit === true,
		canDelete: can?.delete === true,
		isLoading,
		can,
	};
};
